import { ICorporateLocation } from 'corporate/types';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';

/**
 * Import wizard only: with nothing selected yet, seed the required sales locations with the
 * logged-in user's own location. Gated on `isAlternateSales` because the sales-location picker
 * itself only offers alternate-sales locations; coerced with `+` like CorporateLocationHelper,
 * since the flag can arrive as the string "0".
 *
 * The owner arrives already eligibility-filtered from the BE (active + flagged + same company,
 * else null), so it is carried over as-is; the Attributes form's first-sales-location effect is
 * what turns it into the Trade Agreement Owner prefill.
 */
export const seedSalesLocationsFromUserLocation = (
  values: IAttributeForm,
  userLocation?: ICorporateLocation | null
): IAttributeForm => {
  if (values.allSalesLocations || (values.salesLocations?.length ?? 0) > 0) {
    return values;
  }

  if (!userLocation?.id || !+userLocation.isAlternateSales) {
    return values;
  }

  return {
    ...values,
    salesLocations: [
      {
        id: userLocation.id,
        name: userLocation.name ?? '',
        // Backs the chip's "Location ID" line; reducing the entry to {id, name} blanks it.
        externalLocationId: userLocation.externalLocationId ?? '',
        tradeAgreementOwner: userLocation.tradeAgreementOwner ?? null,
      },
    ],
  };
};

/**
 * Import wizard only: a first import starts "Enabled for Selected SPA" on. The BE stores the flag
 * as NOT NULL DEFAULT 0, so the freshly created quote always reports false and cannot tell "never
 * set" from "switched off" — hence the default keys off the import context, not the value. Only
 * for holders of the role resource: for anyone else the toggle is hidden and the key is omitted.
 */
export const applyFirstImportSelectableSpaDefault = (
  values: IAttributeForm,
  {
    isFirstImport,
    canEnableSelectableSpa,
  }: { isFirstImport: boolean; canEnableSelectableSpa: boolean }
): IAttributeForm => {
  if (!isFirstImport || !canEnableSelectableSpa) {
    return values;
  }

  return { ...values, selectableSpaEnabled: true };
};
